import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import DialogLayout from "../DialogLayout";
import validateEmail from "../../Validation/Email";
import {
  FirebaseAuthServiceErrorCode,
  sendPasswordReset,
} from "../../Services/FirebaseService";
import { AppDispatch } from "../../Redux/store";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import Teachr from "../../Objects/models/Teachr";
import PasswordLinkRepository from "../../Repositories/PasswordLinkRepository";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import { BrandButton } from "../shared";
import FormInput from "../Ui/FormInput";

/**
 * Textes selon l'usage : mot de passe oublié (défaut) ou première connexion
 * d'un élève ou client dont le compte a été créé lors de son inscription à
 * l'avance immédiate, sans mot de passe connu. Le mécanisme est le même : un
 * lien Firebase pour choisir le mot de passe.
 */
const WORDING = {
  forgot: {
    trigger: "Mot de passe oublié ?",
    title: "Mot de passe oublié",
    intro: "Entrez l’adresse mail associée à votre compte. Vous allez recevoir un lien pour réinitialiser votre mot de passe.",
    valid: "Recevoir le lien",
    success: "Si cette adresse correspond à un compte, un e-mail vient de partir avec un lien pour choisir votre nouveau mot de passe. Pensez à vérifier vos spams.",
    notFound: "Aucun compte associé à cette adresse mail",
    tooMany: "Un lien vient déjà d'être envoyé à cette adresse. Patientez une minute avant d'en redemander un.",
  },
  firstLogin: {
    trigger: "Recevoir mon lien de première connexion",
    title: "Première connexion",
    intro: "Votre compte a été créé lors de votre inscription, sans mot de passe. Entrez l’adresse e-mail utilisée à l’inscription : vous recevrez un lien pour choisir votre mot de passe, puis vous pourrez vous connecter.",
    valid: "Recevoir mon lien",
    success: "Si cette adresse correspond à un compte, un e-mail vient de partir avec un lien pour choisir votre mot de passe. Consultez votre boîte e-mail (et vos spams), puis revenez vous connecter.",
    notFound: "Aucun compte associé à cette adresse. Vérifiez l’adresse utilisée lors de votre inscription, ou contactez-nous depuis la page d’aide.",
    tooMany: "Un lien vient déjà d'être envoyé à cette adresse. Patientez une minute avant d'en redemander un.",
  },
};

const
  ForgotPassword = ({
    isLogin,
    teachr,
    variant = "forgot",
  }: {
    isLogin: boolean;
    teachr?: Teachr;
    /** « forgot » : mot de passe oublié ; « firstLogin » : première connexion d'un client sans mot de passe. */
    variant?: keyof typeof WORDING;
  }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { classes, common } = useTailwindBrand();
    const wording = WORDING[variant];
    const [open, setOpen] = useState(false);
    const [resetMail, setResetMail] = useState<string>("");
    const [errorresetMail, setErrorResetMail] = useState({
      error: false,
      msg: "",
    });
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
      setOpen(false);
      setResetMail("");
    };
    const validate = () => {
      let error = false;
      if (validateEmail(resetMail).isValid) {
        if (!isLogin) {
          if (resetMail.toLowerCase() === teachr?.mail?.toLowerCase()) {
            setErrorResetMail({
              error: false,
              msg: validateEmail(resetMail).message,
            });
            error = true;
          } else {
            setErrorResetMail({
              error: true,
              msg: "Veuillez entrer l'adresse e-mail associé à ce compte",
            });
            error = false;
          }
        } else {
          setErrorResetMail({
            error: false,
            msg: validateEmail(resetMail).message,
          });
          error = true;
        }
      } else {
        setErrorResetMail({
          error: true,
          msg: validateEmail(resetMail).message,
        });
        error = false;
      }
      return error;
    };
    const handleResetPassword = async () => {
      if (!validate()) return;
      // Le mail part de notre serveur (modèles Firebase verrouillés) et mène à
      // la page /auth/action de l'application. Repli sur l'envoi Firebase si
      // l'API ne répond pas, pour ne jamais bloquer l'utilisateur.
      try {
        const response: any = await PasswordLinkRepository.request(resetMail, variant === "firstLogin" ? "first_login" : "reset");
        handleClose();
        if (response?.status === 429 || response?.code === 429) {
          dispatch({ type: SHOW_ERROR, payload: wording.tooMany });
          return;
        }
        dispatch({ type: SHOW_SUCCESS, payload: wording.success });
      } catch (apiError: any) {
        if (apiError?.status === 429 || apiError?.code === 429 || `${apiError?.message ?? apiError}`.includes("too_many_requests")) {
          handleClose();
          dispatch({ type: SHOW_ERROR, payload: wording.tooMany });
          return;
        }
        sendPasswordReset(resetMail)
          .then(() => {
            handleClose();
            dispatch({ type: SHOW_SUCCESS, payload: wording.success });
          })
          .catch((error) => {
            console.warn(error.code);
            handleClose();
            dispatch({
              type: SHOW_ERROR,
              payload: error.code === FirebaseAuthServiceErrorCode.USER_NOT_FOUND ? wording.notFound : error.code,
            });
          });
      }
    };



    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setResetMail(value)
      },
      []
    );

    return (
      <div className="">
        {isLogin ? (
          <button
            type="button"
            onClick={handleOpen}
            className={`underline underline-offset-2 cursor-pointer ${classes.primaryText} font-bold whitespace-nowrap hover:opacity-80 transition-opacity`}
          >
            {wording.trigger}
          </button>
        ) : (
          // <ButtonField
          //   text=
          //   onClick={handleOpen}
          // />
          <BrandButton
            // variant="ghost"
            children="Réinitialiser mon mot de passe"
            onClick={handleOpen}
            className={` underline cursor-pointer`}
          />
        )}

        <DialogLayout
          title={
            isLogin ? wording.title : "Réinitialise ton mot de passe"
          }
          text={
            <div className={`min-h-[170px] space-y-4 ${common.body}`}>
              <p className={`${common.body}`}>
                {wording.intro}
              </p>
              <FormInput
                label="Email"
                name="email"
                type="text"
                placeholder="Ex : johnxxx@gmail.com"
                value={resetMail}
                error={errorresetMail.msg}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>
          }
          valid={isLogin ? wording.valid : "Réinitialiser mon mot de passe"}
          cancel="Annuler"
          open={open}
          handleClose={handleClose}
          handleSubmit={handleResetPassword}
        />
      </div>
    );
  };

export default ForgotPassword;
